import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { noop } from 'lodash';
import { FormattedMessage } from 'react-intl';

import { Col, Row } from 'react-flexbox-grid';
import { FormError, Input, PhoneInput, Select, Textarea } from 'shared/components/reduxFormComponents';
import requestsStatuses from 'modules/common/constants/requestsStatuses';
import DateOfBirth from 'modules/user/components/_forms/UserDetailsForm/components/DateOfBirth';
import { RestrictedUserField } from 'modules/appointmentsManagement/components/_forms/CreateAppointmentForm/components';
import { Button } from 'shared/components';

import { localErrorMessagesMap } from 'modules/auth/constants/localErrorCodes';
import { apiErrorMessagesMap } from 'modules/auth/constants/apiErrorCodes';
import { localErrorMessagesMap as userLocalErrorMessagesMap } from 'modules/user/constants/localErrorCodes';
import { apiErrorMessagesMap as userApiErrorMessagesMap } from 'modules/user/constants/apiErrorCodes';
import { userAttributeLabels } from 'modules/user/constants/userAttributeLabels';

import theme from './SecondaryLoginForm.scss';

const propTypes = {
  intl: PropTypes.object.isRequired,
  untouch: PropTypes.func.isRequired,
  userTitleOptions: PropTypes.array,
  userGenderOptions: PropTypes.array,
  disabled: PropTypes.bool,
  viewportRef: PropTypes.func,
  readable: PropTypes.bool,
  submitFormStatus: PropTypes.string,
  recaptchaRequired: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  hideExtendedForm: PropTypes.func.isRequired,
  loginConfig: PropTypes.object,
  error: PropTypes.any,
};

const defaultProps = {
  userTitleOptions: [],
  userGenderOptions: [],
  disabled: false,
  viewportRef: noop,
  readable: true,
  submitFormStatus: requestsStatuses.default,
  recaptchaRequired: false,
  loginConfig: {
    secondaryLoginFields: [],
    secondaryLoginText: '',
  },
  error: null,
};

const errorMessagesMap = {
  ...apiErrorMessagesMap,
  ...localErrorMessagesMap,
  ...userLocalErrorMessagesMap,
  ...userApiErrorMessagesMap,
};

const removeFocus = () => document.activeElement.blur();

const SecondaryLoginForm = ({
  intl,
  untouch,
  userTitleOptions,
  userGenderOptions,
  viewportRef,
  disabled,
  readable,
  submitFormStatus,
  recaptchaRequired,
  handleSubmit,
  onSubmit,
  hideExtendedForm,
  loginConfig,
  error,
}) => {
  return (
    <div className={ theme.root } ref={ viewportRef } >
      <h2 className={ theme.title }>
        { loginConfig.secondaryLoginText }
      </h2>
      <form onSubmit={ handleSubmit(onSubmit) }>
        <Row>
          <If condition={ loginConfig.secondaryLoginFields.includes('firstName') }>
            <Col xs={ 12 } className={ theme.fieldset }>
              <Field
                name="firstName"
                testId="userName"
                component={ !readable ? RestrictedUserField : Input }
                label={ userAttributeLabels.firstName }
                placeholder={{
                  id: 'MyAccount.Details.Edit.GivenName.Placeholder',
                  defaultMessage: '** Type user’s given name',
                }}
                tooltipTestId="user-name-tooltip"
                tooltipSize="medium"
                errorMessagesMap={ errorMessagesMap }
                maxLength={ 200 }
                onBlur={ () => { untouch('firstName'); } }
                disabled={ disabled }
              />
            </Col>
          </If>

          <If condition={ loginConfig.secondaryLoginFields.includes('middleName') }>
            <Col xs={ 12 } className={ theme.fieldset }>
              <Field
                name="middleName"
                testId="userMiddleName"
                component={ !readable ? RestrictedUserField : Input }
                label={ userAttributeLabels.middleName }
                placeholder={{
                  id: 'MyAccount.Details.Edit.MiddleName.Placeholder',
                  defaultMessage: '** Type user’s middle name',
                }}
                maxLength={ 200 }
                onBlur={ () => { untouch('middleName'); } }
                disabled={ disabled }
                errorMessagesMap={ errorMessagesMap }
              />
            </Col>
          </If>

          <If condition={ loginConfig.secondaryLoginFields.includes('lastName') }>
            <Col xs={ 12 } className={ theme.fieldset }>
              <Field
                name="lastName"
                testId="userLastName"
                component={ !readable ? RestrictedUserField : Input }
                label={ userAttributeLabels.lastName }
                placeholder={{
                  id: 'MyAccount.Details.Edit.FamilyName.Placeholder',
                  defaultMessage: '** Type user’s given name',
                }}
                tooltipSize="medium"
                tooltipTestId="user-last-name-tooltip"
                errorMessagesMap={ errorMessagesMap }
                maxLength={ 200 }
                onBlur={ () => { untouch('lastName'); } }
                disabled={ disabled }
              />
            </Col>
          </If>

          <If condition={ loginConfig.secondaryLoginFields.includes('title') }>
            <Col xs={ 12 } className={ theme.fieldset }>
              <Field
                name="title"
                testId="userTitle"
                component={ !readable ? RestrictedUserField : Select }
                items={ userTitleOptions }
                label={ userAttributeLabels.title }
                placeholder={{
                  id: 'MyAccount.Details.Edit.UserTitle.Placeholder',
                  defaultMessage: '** Select title',
                }}
                loadingMessage={{
                  id: 'const.pleaseWait',
                  defaultMessage: '** Please Wait...',
                }}
                clearable={ false }
                onBlur={ () => { untouch('title'); } }
                viewportRef={ viewportRef }
                disabled={ disabled }
                errorMessagesMap={ errorMessagesMap }
              />
            </Col>
          </If>

          <If condition={ loginConfig.secondaryLoginFields.includes('gender') }>
            <Col xs={ 12 } className={ theme.fieldset }>
              <Field
                name="gender"
                testId="userGender"
                component={ !readable ? RestrictedUserField : Select }
                items={ userGenderOptions }
                label={ userAttributeLabels.gender }
                placeholder={{
                  id: 'MyAccount.Details.Edit.Gender.Placeholder',
                  defaultMessage: '** Select user’s gender',
                }}
                loadingMessage={{
                  id: 'const.pleaseWait',
                  defaultMessage: '** Please Wait...',
                }}
                clearable={ false }
                onBlur={ () => { untouch('gender'); } }
                viewportRef={ viewportRef }
                disabled={ disabled }
                errorMessagesMap={ errorMessagesMap }
              />
            </Col>
          </If>

          <If condition={ loginConfig.secondaryLoginFields.includes('dateOfBirth') }>
            <Col xs={ 12 } className={ theme.fieldset }>
              <Field
                name="dateOfBirth"
                testId="dateOfBirth"
                component={ !readable ? RestrictedUserField : DateOfBirth }
                label={ userAttributeLabels.dateOfBirth }
                errorMessagesMap={ errorMessagesMap }
                onBlur={ () => { untouch('dateOfBirth'); } }
                disabled={ disabled }
                dateFormat={ loginConfig.birthDateFormat }
              />
            </Col>
          </If>

          <If condition={ loginConfig.secondaryLoginFields.includes('email') }>
            <Col xs={ 12 } className={ theme.fieldset }>
              <Field
                name="email"
                parse={ value => value.trim() }
                component={ !readable ? RestrictedUserField : Input }
                testId="userEmail"
                label={ userAttributeLabels.email }
                placeholder={{
                  id: 'MyAccount.Details.Edit.Email.Placeholder',
                  defaultMessage: '** Type user’s email address',
                }}
                disabled={ disabled }
                tooltipSize="medium"
                tooltipTestId="user-email-tooltip"
                errorMessagesMap={ errorMessagesMap }
                onBlur={ () => { untouch('email'); } }
                inputMode="email"
              />
            </Col>
          </If>

          <If condition={ loginConfig.secondaryLoginFields.includes('phoneNumber') }>
            <Col xs={ 12 } className={ theme.fieldset }>
              <Field
                name="phoneNumber"
                component={ !readable ? RestrictedUserField : PhoneInput }
                label={ userAttributeLabels.phoneNumber }
                tooltipTestId="user-phone-tooltip"
                tooltipSize="medium"
                errorMessagesMap={ errorMessagesMap }
                onBlur={ () => { untouch('phoneNumber'); } }
                disabled={ disabled }
              />
            </Col>
          </If>

          <If condition={ loginConfig.secondaryLoginFields.includes('address') }>
            <Col xs={ 12 } className={ theme.fieldset }>
              <Field
                name="address"
                testId="user-address"
                component={ !readable ? RestrictedUserField : Textarea }
                rootClassName={ theme.textarea }
                label={ userAttributeLabels.address }
                placeholder={{
                  id: 'MyAccount.Details.Edit.Address.Placeholder',
                  defaultMessage: '** Type user’s Address',
                }}
                onBlur={ () => { untouch('address'); } }
                autoExpandable
                disabled={ disabled }
                errorMessagesMap={ errorMessagesMap }
              />
            </Col>
          </If>

          <If condition={ loginConfig.secondaryLoginFields.includes('description') }>
            <Col xs={ 12 } className={ theme.fieldset }>
              <Field
                name="description"
                testId="profile-title"
                component={ !readable ? RestrictedUserField : Textarea }
                rootClassName={ theme.textarea }
                label={ userAttributeLabels.description }
                placeholder={{
                  id: 'MyAccount.Details.Edit.Profile.Placeholder',
                  defaultMessage: '** Type user’s Profile',
                }}
                maxLength={ 200 }
                onBlur={ () => { untouch('description'); } }
                disabled={ disabled }
                errorMessagesMap={ errorMessagesMap }
              />
            </Col>
          </If>
        </Row>

        <If condition={ error }>
          <FormError error={ error } className={ theme.error } />
        </If>

        <div
          data-testId="login-i-have-id-number"
          onClick={ hideExtendedForm }
          className={ theme.link }
        >
          <FormattedMessage
            id="Authentication.Login.Patient.Label.HaveIdNumber"
            defaultMessage="** I have ID Number"
          />
        </div>

        <Button
          testId="login-button"
          label={ intl.formatMessage({ id: 'General.Next', defaultMessage: '** Next' }) }
          iconName="arrow-right"
          isLoading={ submitFormStatus === requestsStatuses.pending || submitFormStatus === requestsStatuses.success }
          size="large"
          disabled={
            submitFormStatus === requestsStatuses.pending
            || submitFormStatus === requestsStatuses.success
            || recaptchaRequired
          }
          mediaResized
          fullWidth
          onClick={ removeFocus }
        />
      </form>
    </div>
  );
};

SecondaryLoginForm.propTypes = propTypes;
SecondaryLoginForm.defaultProps = defaultProps;

export default reduxForm({
  touchOnBlur: false,
})(SecondaryLoginForm);
